
import { useState } from "react";
import { CirclePlus, DollarSign, Globe, Headset, Shield, Sparkles, TicketPercent, TrendingUp, TriangleAlert, Wallet, } from "lucide-react";
import { InshurClaimCta } from "./InshurClaimCta";
import { SmartLink } from "./SmartLink";
import { LINKS } from "../homeLinks";
const PRODUCT_LOGOS = [
    { name: "rentdirect", src: "/images/products/vertical/rentdirect.png" },
    { name: "rentshield", src: "/images/products/vertical/rentshield.png" },
    { name: "rentID", src: "/images/products/vertical/rentid.png" },
    { name: "rentsuccess", src: "/images/products/vertical/rentsuccess.png" },
    { name: "rentAI", src: "/images/products/vertical/rentai.png" },
    { name: "rentacademy", src: "/images/products/vertical/rentacademy.png" },
];
const PLANS = [
    {
        id: "starter",
        type: "PAYGO",
        name: "Starter Plan",
        price: "$7",
        unit: "/ rental day",
        summary: "Start for free. Only pay when the vehicle is rented. Up to five vehicles. Upgrade to Pro anytime.",
        features: [
            { icon: TrendingUp, title: "Ease into Direct Rentals", description: "Start fast and rent directly as needed" },
            { icon: Globe, title: "Rental Website included", description: "Fully responsive booking website." },
            { icon: TrendingUp, title: "Rental Back Office included", description: "Automated operations, logistics & admin." },
            { icon: TriangleAlert, title: "Collect 80% on incidentals", description: "Profit from rental policy resolutions." },
            { icon: Sparkles, title: "RentAI Unlimited", description: "Collaborative AI Rental CoPilot.", tag: "14-Day Trial" },
            { icon: Wallet, title: "Tolls & Tickets", description: "Automated toll and ticket management.", tag: "Add-On" },
            { icon: Shield, title: "Roadside Assistance", description: "24/7 roadside support for your fleet.", tag: "Add-On" },
        ],
        cta: "Continue with Starter Plan",
    },
    {
        id: "pro",
        type: "SUBSCRIPTION",
        popular: true,
        name: "Pro Plan",
        price: "From $99",
        unit: "/ month",
        summary: "Unlimited rentals. Save monthly. Mix & match plans. Unlock extra revenue. Major discounts.",
        intro: "Everything in Starter Plan, plus:",
        features: [
            { icon: Sparkles, title: "RentAI Unlimited", description: "Collaborative AI Rental CoPilot." },
            { icon: TriangleAlert, title: "Collect 85% on incidentals", description: "Profit from rental policy resolutions." },
            { icon: CirclePlus, title: "Create Custom Extras", description: "Add custom products and services." },
            { icon: DollarSign, title: "Extra Revenue Channels", description: "Add profit to Rentid embedded services." },
            { icon: Headset, title: "Platform Onboarding Included", description: "Start up guidance from from industry experts." },
            { icon: Wallet, title: "Tolls & Tickets", description: "Automated toll and ticket management.", tag: "10% off" },
            { icon: Shield, title: "Roadside Assistance", description: "24/7 roadside support for your fleet.", tag: "10% off" },
        ],
        cta: "Continue with Pro Plan",
    },
];
function getProPricing(fleetSize) {
    if (fleetSize <= 5)
        return { total: 99, note: "Includes up to 5 vehicles" };
    if (fleetSize <= 10)
        return { total: 199, note: "Includes up to 10 vehicles" };
    const tierTwoVehicles = Math.min(fleetSize, 25) - 10;
    const tierThreeVehicles = Math.max(0, fleetSize - 25);
    // Owner (2026-10-01): no note above 10 vehicles.
    return { total: 199 + tierTwoVehicles * 10 + tierThreeVehicles * 8, note: "" };
}
function FleetSizeInput({ value, onChange }) {
    return (<label className="-my-1 shrink-0">
      <span className="sr-only">Fleet size</span>
      <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="Fleet size" value={value} onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} className="h-11 w-24 rounded-lg sm:w-28 border border-white/15 bg-white/[0.06] px-3 text-center text-sm font-semibold text-white outline-none transition-colors placeholder:font-medium placeholder:text-white/50 focus:border-[#33F7BE]/70 focus:ring-2 focus:ring-[#33F7BE]/30"/>
    </label>);
}
/* `promo` = the INSHUR landing page variant (30-day-free badge, struck-through price, Claim CTA).
   Without it (home page, owner 2026-10-01) the cards show standard pricing only. */
function PlanCard({ plan, promo }) {
    const detailsId = `${plan.id}-plan-details`;
    const isPro = plan.id === "pro";
    const [fleetInput, setFleetInput] = useState("");
    const fleetSize = Math.max(1, Number.parseInt(fleetInput, 10) || 1);
    const proPricing = getProPricing(fleetSize);
    return (<article className={`relative flex flex-col rounded-3xl border bg-[#141414] md:row-span-2 md:grid md:grid-rows-subgrid md:gap-y-0 ${plan.popular ? "border-[#33F7BE]/60 shadow-[0_0_60px_-20px_rgba(51,247,190,0.35)]" : "border-white/10"}`}>
      {plan.popular && (<div className={`absolute inset-x-3 top-0 flex -translate-y-1/2 items-center justify-center gap-2 sm:inset-x-8 ${promo ? "sm:justify-between" : "sm:justify-end"}`}>
          {promo && (<span className="relative inline-flex min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full border border-white/10 bg-[#262626] px-2.5 py-1.5 text-[11px] font-semibold text-white sm:gap-2 sm:px-3 sm:text-xs">
            <span aria-hidden="true" className="badge-shimmer pointer-events-none absolute inset-y-0 left-0 w-1/3"/>
            <TicketPercent aria-hidden="true" className="relative size-3.5 shrink-0 sm:size-4"/>
            <span className="relative">Access Pro Plan Free for 30 Days</span>
          </span>)}
          <span className="hidden w-28 shrink-0 justify-center sm:flex">
            <span className="inline-flex whitespace-nowrap rounded-full bg-[#33F7BE] px-3 py-1 text-xs font-semibold text-black">
              Most Popular
            </span>
          </span>
        </div>)}
      <div className="flex flex-col p-5 sm:p-8">
        <div className="flex items-start justify-between gap-3 sm:gap-4">
          <h3 className="whitespace-nowrap font-sans text-2xl font-semibold tracking-tight text-white sm:text-3xl">{plan.name}</h3>
          {isPro && <FleetSizeInput value={fleetInput} onChange={setFleetInput}/>}
        </div>
        {isPro && !promo ? (<p className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1" aria-live="polite">
            <span className="font-sans text-4xl font-semibold tracking-tight text-white sm:text-5xl">{`$${proPricing.total.toLocaleString("en-US")}`}</span>
            <span className="text-base text-white/50">{plan.unit}</span>
            {proPricing.note && <span className="w-full text-sm text-white/60">{proPricing.note}</span>}
          </p>) : isPro ? (<p className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1" aria-live="polite">
            <span className="flex items-baseline gap-x-2 whitespace-nowrap">
              <span className="sr-only">Regular price</span>
              <s className="font-sans text-4xl font-semibold tracking-tight text-white/40 decoration-[#ec4899] decoration-2 sm:text-5xl">
                {`$${proPricing.total.toLocaleString("en-US")}`}
              </s>
              <span className="text-base text-white/40 line-through">{plan.unit}</span>
            </span>
            <span className="flex items-baseline gap-x-2 whitespace-nowrap">
              <span className="font-sans text-2xl font-semibold text-white">$0</span>
              <span className="text-base text-white/70">first 30 days</span>
            </span>
            {/* Owner (2026-10-01): what the quoted price covers for the entered fleet size. */}
            {proPricing.note && <span className="w-full text-sm text-white/60">{proPricing.note}</span>}
          </p>) : (<p className="mt-2 flex items-baseline gap-2">
            <span className="font-sans text-4xl font-semibold tracking-tight text-white sm:text-5xl">{plan.price}</span>
            <span className="text-base text-white/50">{plan.unit}</span>
          </p>)}
        <p className="mt-4 text-pretty text-base leading-relaxed text-white/60">{plan.summary}</p>
      </div>

      <div id={detailsId} className="border-t border-white/10 px-5 py-6 sm:px-8">
        {!isPro && (<ul aria-label="Included Rentid products" className="mb-6 flex flex-nowrap items-center gap-x-1">
            {PRODUCT_LOGOS.map((logo) => (<li key={logo.name} className="aspect-[4/3] min-w-0 flex-1 overflow-hidden">
  <img src={logo.src || "/placeholder.svg"} alt={logo.name} className="h-full w-full scale-125 object-contain"/>
              </li>))}
          </ul>)}
        {plan.intro && (<p className="mb-8 flex items-center gap-2 text-sm font-semibold text-white">
            <Sparkles aria-hidden="true" className="size-4 text-[#33F7BE]"/>
            {plan.intro}
          </p>)}
        <ul className="flex flex-col gap-5">
          {plan.features.map((feature) => (<li key={feature.title} className="flex items-start gap-4">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-white">
                <feature.icon aria-hidden="true" className="size-[18px]"/>
              </span>
              <div className="flex flex-col">
                <span className="flex flex-wrap items-center gap-2 text-base font-semibold text-white">
                  {feature.title}
                  {feature.tag && (<span className="rounded-full border border-white/15 px-2.5 py-0.5 text-xs font-medium text-white/70">
                      {feature.tag}
                    </span>)}
                </span>
                <span className="text-sm leading-relaxed text-white/55">{feature.description}</span>
              </div>
            </li>))}
        </ul>
      </div>

    </article>);
}
export function InshurPlans({ promo = false } = {}) {
    return (<section id="plans" aria-labelledby="plans-heading" className="relative w-full scroll-mt-24 overflow-hidden rounded-t-[2.5rem] border-t border-white/[0.1] bg-[#0A0A0A] px-5 py-16 sm:px-8 sm:py-20 lg:rounded-t-[3rem] md:px-16 lg:py-24">
      <div className="mx-auto flex w-full max-w-5xl flex-col items-center">
        <h2 id="plans-heading" className="text-balance text-center font-sans text-4xl font-light leading-[1.05] tracking-tight text-white sm:text-5xl md:text-6xl">
          Choose your plan
        </h2>
        <p className="mt-4 max-w-xl text-pretty text-center text-base leading-relaxed text-white/60 sm:text-lg">
          Start free or go Pro for unlimited rentals and bigger savings.
        </p>
        {promo ? (<InshurClaimCta className="mt-8"/>) : (<SmartLink href={LINKS.getStarted} className="mt-8 inline-flex items-center justify-center rounded-full px-8 py-4 text-base font-semibold text-black hover:bg-[#2be0a8] active:scale-95 sm:py-3.5" style={{ backgroundColor: "#33F7BE" }}>
            Start for free
          </SmartLink>)}
        <div className="mt-12 grid w-full grid-cols-1 items-start gap-6 md:grid-cols-2 lg:mt-16 lg:gap-8">
          {PLANS.map((plan) => (<PlanCard key={plan.id} plan={plan} promo={promo}/>))}
        </div>
      </div>
    </section>);
}
